import React, { useState, useEffect } from 'react';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectLabel, SelectGroup,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuLabel,
} from '@kuda/ui';
import { ChevronDownIcon } from '@kuda/ui/icons';
import { config } from './config';

export default function SelectCluster({
  onClusterChange,
  style,
  label,
  defaultCluster = '',
  defaultClusters = [],
  multiSelect = false,
  filterEnv,
  showAll = false,
  userRole // string OR array — we handle both for backwards compatibility
}) {
  let clusters_all = [];
  try {
    clusters_all = JSON.parse(import.meta.env.VITE_CLUSTERS || '[]');
  } catch {
    clusters_all = [];
  }

  // Normalize cluster list
  const normalizedClusters = clusters_all.map((c) => ({
    value: c.value,
    label: c.label ?? c.value,
    env: (c.env || '').toLowerCase(),
  }));

  const normalizeFilter = (e) => {
    if (!e) return null;
    const v = e.toString().trim().toLowerCase();
    if (v === 'test') return 'test';
    if (v === 'staging' || v === 'prod' || v === 'production') return 'prod';
    return null;
  };

  const filterKey = normalizeFilter(filterEnv);

  const filteredClusters = showAll
    ? normalizedClusters
    : filterKey
    ? normalizedClusters.filter((c) => c.env === filterKey)
    : normalizedClusters;

  const testClusters = filteredClusters.filter((c) => c.env === 'test' || c.env === 'dev');
  const prodClusters = filteredClusters.filter((c) => c.env === 'prod');

  // Support both: userRole as a string (legacy) or as an array (new)
  const rolesArray = Array.isArray(userRole)
    ? userRole
    : userRole
    ? [userRole]
    : [];

  // User can access prod if ANY of their roles is in prodAccessRoles
  const canAccessProd = rolesArray.some((r) =>
    config.rbac.prodAccessRoles.includes(r)
  );

  const [cluster, setCluster] = useState(defaultCluster);
  const [clusters, setClusters] = useState(defaultClusters);

  useEffect(() => {
    if (multiSelect) {
      const valid = clusters.filter((val) =>
        filteredClusters.some((fc) => fc.value === val)
      );
      if (valid.length !== clusters.length) {
        setClusters(valid);
        onClusterChange(valid);
      }
    } else {
      if (cluster && !filteredClusters.some((fc) => fc.value === cluster)) {
        setCluster('');
        onClusterChange('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, showAll]);

  const handleSingleChange = (value) => {
    setCluster(value);
    onClusterChange(value);
  };

  const handleMultiToggle = (value, checked) => {
    setClusters((prev) => {
      const next = checked ? [...prev, value] : prev.filter((v) => v !== value);
      onClusterChange(next);
      return next;
    });
  };

  const renderValueLabel = () => {
    if (multiSelect) {
      if (!clusters.length) return null;
      return clusters
        .map((v) => filteredClusters.find((fc) => fc.value === v)?.label || v)
        .join(', ');
    }
    return filteredClusters.find((fc) => fc.value === cluster)?.label || cluster;
  };

  return (
    <div className="flex flex-col gap-1.5" style={{ minWidth: 120, ...style }}>
      {label && <span className="text-sm text-color-text-primary">{label}</span>}

      {multiSelect ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="group flex h-10 w-full items-center justify-between rounded px-3 py-2 font-body text-md text-color-text-primary border-0 bg-color-bg-primary hover:bg-color-bg-secondary"
            >
              <span className={clusters.length ? '' : 'text-color-text-placeholder'}>
                {renderValueLabel() || 'Select clusters'}
              </span>
              <ChevronDownIcon className="size-4 text-color-icon-primary" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
            {testClusters.length > 0 && <DropdownMenuLabel>Test</DropdownMenuLabel>}
            {testClusters.map((clusterItem) => (
              <DropdownMenuCheckboxItem
                key={clusterItem.value}
                checked={clusters.indexOf(clusterItem.value) > -1}
                onCheckedChange={(checked) => handleMultiToggle(clusterItem.value, checked)}
                onSelect={(e) => e.preventDefault()}
              >
                {clusterItem.label}
              </DropdownMenuCheckboxItem>
            ))}

            {canAccessProd && prodClusters.length > 0 && (
              <>
                <DropdownMenuLabel>Staging / Prod</DropdownMenuLabel>
                {prodClusters.map((clusterItem) => (
                  <DropdownMenuCheckboxItem
                    key={clusterItem.value}
                    checked={clusters.indexOf(clusterItem.value) > -1}
                    onCheckedChange={(checked) => handleMultiToggle(clusterItem.value, checked)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {clusterItem.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <Select value={cluster} onValueChange={handleSingleChange}>
          <SelectTrigger>
            <SelectValue placeholder={label}>{renderValueLabel()}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {testClusters.length > 0 && (
              <SelectGroup>
                <SelectLabel>Test</SelectLabel>
                {testClusters.map((clusterItem) => (
                  <SelectItem key={clusterItem.value} value={clusterItem.value}>
                    {clusterItem.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            )}

            {canAccessProd && prodClusters.length > 0 && (
              <SelectGroup>
                <SelectLabel>Staging / Prod</SelectLabel>
                {prodClusters.map((clusterItem) => (
                  <SelectItem key={clusterItem.value} value={clusterItem.value}>
                    {clusterItem.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>
      )}

      {!canAccessProd && prodClusters.length > 0 && (
        <span className="text-xs text-color-text-state-error-default">
          You do not have permission to view Staging / Prod clusters.
        </span>
      )}
    </div>
  );
}
